import React, { useState, useEffect } from 'react';
import { getAdminCategories, createCategory, updateCategory, deleteCategory, getCategory } from '../../services/api';
import AddCategoryModal from '../../components/AddCategoryModal';
import DeleteCategoryModal from '../../components/DeleteCategoryModal';
import { useToast } from '../../contexts/ToastContext';

function Categories() {
  const { showSuccess, showError } = useToast();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all, active, inactive
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState(new Set()); // Track expanded main categories

  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      if (!token) {
        showError('Authentication required. Please log in again.');
        return;
      }
      const data = await getAdminCategories(token);
      setCategories(data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      showError(error.message || 'Failed to load categories. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = async (categoryData) => {
    setSubmitLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        const error = new Error('Authentication required. Please log in again.');
        showError(error.message);
        setSubmitLoading(false);
        throw error;
      }
      await createCategory(categoryData, token);
      showSuccess('Category created successfully!');
      await fetchCategories();
      setIsAddModalOpen(false);
    } catch (error) {
      showError(error.message || 'Failed to create category. Please try again.');
      setSubmitLoading(false);
      throw error;
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEditCategory = async (categoryId) => {
    try {
      const token = localStorage.getItem('auth_token');
      const category = await getCategory(categoryId, token);
      setSelectedCategory(category);
      setIsEditModalOpen(true);
    } catch (error) {
      showError(error.message || 'Failed to load category for editing.');
    }
  };

  const handleUpdateCategory = async (categoryData) => {
    if (!selectedCategory) return;
    
    // Debug: Log what we're receiving
    console.log('🔍 handleUpdateCategory - categoryData:', categoryData);
    console.log('🔍 handleUpdateCategory - name:', categoryData.name);
    console.log('🔍 handleUpdateCategory - name type:', typeof categoryData.name);
    
    setSubmitLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        const error = new Error('Authentication required. Please log in again.');
        showError(error.message);
        setSubmitLoading(false);
        throw error;
      }
      await updateCategory(selectedCategory.id, categoryData, token);
      showSuccess('Category updated successfully!');
      await fetchCategories();
      setIsEditModalOpen(false);
      setSelectedCategory(null);
    } catch (error) {
      showError(error.message || 'Failed to update category. Please try again.');
      setSubmitLoading(false);
      throw error;
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    setSubmitLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        const error = new Error('Authentication required. Please log in again.');
        showError(error.message);
        setSubmitLoading(false);
        throw error;
      }
      await deleteCategory(categoryId, token);
      showSuccess('Category deleted successfully!');
      await fetchCategories();
      setIsDeleteModalOpen(false);
      setSelectedCategory(null);
    } catch (error) {
      showError(error.message || 'Failed to delete category. Please try again.');
      setSubmitLoading(false);
      throw error;
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDeleteClick = async (categoryId) => {
    try {
      const token = localStorage.getItem('auth_token');
      const category = await getCategory(categoryId, token);
      setSelectedCategory(category);
      setIsDeleteModalOpen(true);
    } catch (error) {
      showError(error.message || 'Failed to load category details.');
    }
  };

  // Separate main categories and subcategories
  const mainCategories = categories.filter(cat => !cat.parent_id);
  const subcategories = categories.filter(cat => cat.parent_id);

  // Filter categories
  const filteredMainCategories = mainCategories.filter(category => {
    const matchesSearch = category.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (category.description && category.description.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesStatus = statusFilter === 'all' ||
                         (statusFilter === 'active' && category.is_active) ||
                         (statusFilter === 'inactive' && !category.is_active);
    
    return matchesSearch && matchesStatus;
  });

  // Get subcategories for each main category
  const getSubcategoriesForParent = (parentId) => {
    return subcategories.filter(sub => sub.parent_id === parentId);
  };

  // Calculate total items (main categories + their subcategories)
  const getTotalItemsForDisplay = () => {
    return filteredMainCategories.reduce((total, mainCat) => {
      const subs = getSubcategoriesForParent(mainCat.id);
      return total + 1 + subs.length; // 1 for main category + subcategories
    }, 0);
  };

  // Pagination logic - count main categories with their subcategories
  const getPaginatedCategories = () => {
    let itemsCount = 0;
    const result = [];
    
    for (const mainCat of filteredMainCategories) {
      const subs = getSubcategoriesForParent(mainCat.id);
      const totalItemsForThisMain = 1 + subs.length; // Main + subcategories
      
      // Check if we should include this main category
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      
      if (itemsCount < endIndex && (itemsCount + totalItemsForThisMain) > startIndex) {
        result.push({ main: mainCat, subcategories: subs });
      }
      
      itemsCount += totalItemsForThisMain;
      
      // Stop if we've reached the end of the page
      if (itemsCount >= endIndex) {
        break;
      }
    }
    
    return result;
  };

  const paginatedCategories = getPaginatedCategories();
  const totalItems = getTotalItemsForDisplay();
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, itemsPerPage]);

  // Expand all categories by default when categories are loaded
  useEffect(() => {
    if (categories.length > 0 && expandedCategories.size === 0 && mainCategories.length > 0) {
      const mainCategoryIds = mainCategories.map(cat => cat.id);
      setExpandedCategories(new Set(mainCategoryIds));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories.length, mainCategories.length]);

  // Toggle category expansion
  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  // Expand/Collapse all categories
  const toggleAllCategories = () => {
    if (expandedCategories.size === filteredMainCategories.length) {
      // Collapse all
      setExpandedCategories(new Set());
    } else {
      // Expand all
      const allIds = filteredMainCategories.map(cat => cat.id);
      setExpandedCategories(new Set(allIds));
    }
  };

  // Stats
  const totalCategories = categories.length;
  const totalMainCategories = mainCategories.length;
  const totalSubcategories = subcategories.length;
  const activeCategories = categories.filter(c => c.is_active).length;
  const inactiveCategories = categories.filter(c => !c.is_active).length;
  const totalProducts = categories.reduce((sum, cat) => sum + (cat.products_count || 0), 0);

  return (
    <div>
      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6 mb-8">
        <div className="flex items-center gap-3">
          <div className="w-1.5 h-10 bg-gradient-to-b from-primary via-primary to-primary-dark rounded-full"></div>
          <div>
            <h1 className="text-4xl font-bold text-slate-900 tracking-tight mb-2">Categories</h1>
            <p className="text-slate-600 font-medium">Organize your products by categories</p>
          </div>
        </div>
        <button 
          onClick={() => setIsAddModalOpen(true)}
          className="group relative overflow-hidden bg-gradient-to-r from-primary via-primary to-primary-dark text-white px-7 py-3.5 rounded-2xl hover:shadow-2xl hover:shadow-primary/30 transition-all duration-300 transform hover:-translate-y-0.5 font-semibold flex items-center gap-2.5 whitespace-nowrap border border-primary/20"
        >
          <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          <svg className="w-5 h-5 relative z-10 group-hover:rotate-90 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
          </svg>
          <span className="relative z-10">Add New Category</span>
        </button>
      </div>

      {/* Search and Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        {/* Search Input */}
        <div className="flex-1 relative">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            type="text"
            placeholder="Search categories..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200 text-sm font-medium"
          />
        </div>

        {/* Status Filter */}
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200 text-sm font-semibold cursor-pointer"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>

        {/* Items Per Page Filter */}
        <select
          value={itemsPerPage}
          onChange={(e) => setItemsPerPage(Number(e.target.value))}
          className="px-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200 text-sm font-semibold cursor-pointer"
        >
          <option value="6">6 per page</option>
          <option value="10">10 per page</option>
          <option value="20">20 per page</option>
          <option value="50">50 per page</option>
          <option value="100">100 per page</option>
        </select>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Total Categories */}
        <div className="relative bg-gradient-to-br from-blue-50/80 via-white to-white rounded-2xl border-2 border-blue-300/50 p-3.5 shadow-xl shadow-blue-200/50 hover:shadow-2xl hover:shadow-blue-300/50 hover:-translate-y-1 transition-all duration-500 group overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 to-blue-600 opacity-70"></div>
          <div className="absolute -top-6 -right-6 w-24 h-24 bg-gradient-to-br from-blue-400 to-blue-500 rounded-full opacity-10 blur-2xl group-hover:opacity-20 transition-opacity duration-500"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-2.5">
              <span className="text-xs font-bold text-gray-900 uppercase tracking-wider">Total Categories</span>
              <div className="relative w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center text-white shadow-lg shadow-blue-300/50 group-hover:scale-110 group-hover:rotate-3 group-hover:shadow-2xl transition-all duration-500">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
              </div>
            </div>
            <p className="text-2xl font-black text-blue-700 mb-1 leading-none group-hover:scale-105 transition-transform duration-300">{totalCategories}</p>
            <div className="text-xs text-blue-600 mt-1">{totalMainCategories} main, {totalSubcategories} sub</div>
            <div className="w-10 h-0.5 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full opacity-60 mt-2 group-hover:w-full transition-all duration-500"></div>
          </div>
        </div>

        {/* Active Categories */}
        <div className="relative bg-gradient-to-br from-green-50/80 via-white to-white rounded-2xl border-2 border-green-300/50 p-3.5 shadow-xl shadow-green-200/50 hover:shadow-2xl hover:shadow-green-300/50 hover:-translate-y-1 transition-all duration-500 group overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-green-500 to-green-600 opacity-70"></div>
          <div className="absolute -top-6 -right-6 w-24 h-24 bg-gradient-to-br from-green-400 to-green-500 rounded-full opacity-10 blur-2xl group-hover:opacity-20 transition-opacity duration-500"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-2.5">
              <span className="text-xs font-bold text-gray-900 uppercase tracking-wider">Active</span>
              <div className="relative w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center text-white shadow-lg shadow-green-300/50 group-hover:scale-110 group-hover:rotate-3 group-hover:shadow-2xl transition-all duration-500">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <p className="text-2xl font-black text-green-700 mb-1 leading-none group-hover:scale-105 transition-transform duration-300">{activeCategories}</p>
            <div className="w-10 h-0.5 bg-gradient-to-r from-green-500 to-green-600 rounded-full opacity-60 mt-2 group-hover:w-full transition-all duration-500"></div>
          </div>
        </div>

        {/* Inactive Categories */}
        <div className="relative bg-gradient-to-br from-gray-50/80 via-white to-white rounded-2xl border-2 border-gray-300/50 p-3.5 shadow-xl shadow-gray-200/50 hover:shadow-2xl hover:shadow-gray-300/50 hover:-translate-y-1 transition-all duration-500 group overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-gray-500 to-gray-600 opacity-70"></div>
          <div className="absolute -top-6 -right-6 w-24 h-24 bg-gradient-to-br from-gray-400 to-gray-500 rounded-full opacity-10 blur-2xl group-hover:opacity-20 transition-opacity duration-500"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-2.5">
              <span className="text-xs font-bold text-gray-900 uppercase tracking-wider">Inactive</span>
              <div className="relative w-10 h-10 bg-gradient-to-br from-gray-500 to-gray-600 rounded-lg flex items-center justify-center text-white shadow-lg shadow-gray-300/50 group-hover:scale-110 group-hover:rotate-3 group-hover:shadow-2xl transition-all duration-500">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <p className="text-2xl font-black text-gray-700 mb-1 leading-none group-hover:scale-105 transition-transform duration-300">{inactiveCategories}</p>
            <div className="w-10 h-0.5 bg-gradient-to-r from-gray-500 to-gray-600 rounded-full opacity-60 mt-2 group-hover:w-full transition-all duration-500"></div>
          </div>
        </div>

        {/* Total Products */}
        <div className="relative bg-gradient-to-br from-purple-50/80 via-white to-white rounded-2xl border-2 border-purple-300/50 p-3.5 shadow-xl shadow-purple-200/50 hover:shadow-2xl hover:shadow-purple-300/50 hover:-translate-y-1 transition-all duration-500 group overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-purple-500 to-indigo-600 opacity-70"></div>
          <div className="absolute -top-6 -right-6 w-24 h-24 bg-gradient-to-br from-purple-400 to-indigo-500 rounded-full opacity-10 blur-2xl group-hover:opacity-20 transition-opacity duration-500"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-2.5">
              <span className="text-xs font-bold text-gray-900 uppercase tracking-wider">Total Products</span>
              <div className="relative w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg flex items-center justify-center text-white shadow-lg shadow-purple-300/50 group-hover:scale-110 group-hover:rotate-3 group-hover:shadow-2xl transition-all duration-500">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
            </div>
            <p className="text-2xl font-black text-purple-700 mb-1 leading-none group-hover:scale-105 transition-transform duration-300">{totalProducts}</p>
            <div className="w-10 h-0.5 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-full opacity-60 mt-2 group-hover:w-full transition-all duration-500"></div>
          </div>
        </div>
      </div>

      {/* Categories Table */}
      <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xl overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            <p className="text-gray-600 mt-4 font-medium">Loading categories...</p>
          </div>
        ) : filteredMainCategories.length === 0 ? (
          <div className="p-12 text-center">
            <div className="relative w-24 h-24 mx-auto mb-6">
              <div className="absolute inset-0 bg-gradient-to-br from-slate-100 to-slate-200 rounded-3xl blur-xl opacity-50"></div>
              <div className="relative w-24 h-24 bg-gradient-to-br from-slate-50 to-slate-100 rounded-3xl flex items-center justify-center border-2 border-slate-200/60 shadow-lg">
                <svg className="w-12 h-12 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
              </div>
            </div>
            <p className="text-slate-600 font-semibold text-lg">
              {searchQuery || statusFilter !== 'all' ? 'No categories found' : 'No categories yet'}
            </p>
            <p className="text-slate-400 text-sm mt-2">
              {searchQuery || statusFilter !== 'all' 
                ? 'Try adjusting your search or filters' 
                : 'Get started by creating your first main category'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <span>Category</span>
                      {filteredMainCategories.length > 0 && (
                        <button
                          onClick={toggleAllCategories}
                          className="text-xs text-primary hover:text-primary-dark font-normal px-2 py-1 rounded hover:bg-primary/10 transition-colors"
                          title={expandedCategories.size === filteredMainCategories.length ? 'Collapse All' : 'Expand All'}
                        >
                          {expandedCategories.size === filteredMainCategories.length ? 'Collapse All' : 'Expand All'}
                        </button>
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Description</th>
                  <th className="px-6 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">Products</th>
                  <th className="px-6 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedCategories.map(({ main: category, subcategories: categorySubcategories }) => {
                  return (
                    <React.Fragment key={category.id}>
                      {/* Main Category Row */}
                      <tr className="hover:bg-gray-50 transition-colors bg-gray-50/50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            {/* Expand/Collapse Button */}
                            {categorySubcategories.length > 0 && (
                              <button
                                onClick={() => toggleCategory(category.id)}
                                className="flex-shrink-0 w-6 h-6 flex items-center justify-center text-gray-500 hover:text-primary transition-colors rounded hover:bg-gray-100"
                                title={expandedCategories.has(category.id) ? 'Collapse' : 'Expand'}
                              >
                                <svg 
                                  className={`w-4 h-4 transition-transform duration-200 ${expandedCategories.has(category.id) ? 'rotate-90' : ''}`}
                                  fill="none" 
                                  stroke="currentColor" 
                                  viewBox="0 0 24 24"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </button>
                            )}
                            {categorySubcategories.length === 0 && (
                              <div className="w-6"></div>
                            )}
                            {category.image ? (
                              <img
                                src={category.image}
                                alt={category.name}
                                className="w-12 h-12 object-cover rounded-lg border-2 border-gray-200"
                              />
                            ) : (
                              <div className="w-12 h-12 bg-gradient-to-br from-primary/20 to-primary/30 rounded-lg border-2 border-primary/30 flex items-center justify-center">
                                <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                                </svg>
                              </div>
                            )}
                            <div>
                              <div className="text-sm font-bold text-gray-900 flex items-center gap-2">
                                {category.name}
                                <span className="text-xs font-normal text-primary bg-primary/10 px-2 py-0.5 rounded">Main</span>
                                {categorySubcategories.length > 0 && (
                                  <span className="text-xs font-normal text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                                    {categorySubcategories.length} {categorySubcategories.length === 1 ? 'subcategory' : 'subcategories'}
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-gray-500">{category.slug}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-primary/10 text-primary">
                            Main Category
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-600 max-w-md truncate">
                            {category.description || <span className="text-gray-400 italic">No description</span>}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                            {category.products_count || 0}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                            category.is_active 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {category.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleEditCategory(category.id)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Edit category"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDeleteClick(category.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete category"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                      {/* Subcategories Rows */}
                      {expandedCategories.has(category.id) && categorySubcategories.map((subcategory) => (
                        <tr 
                          key={subcategory.id} 
                          className="hover:bg-gray-50 transition-all duration-200"
                          style={{
                            animation: 'slideDown 0.2s ease-out'
                          }}
                        >
                          <td className="px-6 py-3 whitespace-nowrap">
                            <div className="flex items-center gap-3 pl-8">
                              <div className="w-1 h-8 bg-gray-300 rounded-full"></div>
                              {subcategory.image ? (
                                <img
                                  src={subcategory.image}
                                  alt={subcategory.name}
                                  className="w-10 h-10 object-cover rounded-lg border-2 border-gray-200"
                                />
                              ) : (
                                <div className="w-10 h-10 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg border-2 border-gray-200 flex items-center justify-center">
                                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                                  </svg>
                                </div>
                              )}
                              <div>
                                <div className="text-sm font-semibold text-gray-800">{subcategory.name}</div>
                                <div className="text-xs text-gray-500">{subcategory.slug}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-3">
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
                              Subcategory
                            </span>
                          </td>
                          <td className="px-6 py-3">
                            <div className="text-sm text-gray-600 max-w-md truncate">
                              {subcategory.description || <span className="text-gray-400 italic">No description</span>}
                            </div>
                          </td>
                          <td className="px-6 py-3 text-center">
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                              {subcategory.products_count || 0}
                            </span>
                          </td>
                          <td className="px-6 py-3 text-center">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                              subcategory.is_active 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {subcategory.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-6 py-3 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => handleEditCategory(subcategory.id)}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                title="Edit subcategory"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                              <button
                                onClick={() => handleDeleteClick(subcategory.id)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Delete subcategory"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        {!loading && filteredMainCategories.length > 0 && totalPages > 1 && (
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-sm text-gray-700">
              Showing <span className="font-semibold">{Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)}</span> to{' '}
              <span className="font-semibold">{Math.min(currentPage * itemsPerPage, totalItems)}</span> of{' '}
              <span className="font-semibold">{totalItems}</span> categories
            </div>
            
            <div className="flex items-center gap-2">
              {/* Previous Button */}
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border-2 border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                Previous
              </button>

              {/* Page Numbers */}
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-2 text-sm font-semibold rounded-xl transition-all duration-200 ${
                        currentPage === pageNum
                          ? 'bg-primary text-white shadow-lg'
                          : 'text-gray-700 bg-white border-2 border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              {/* Next Button */}
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border-2 border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <AddCategoryModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={handleAddCategory}
        loading={submitLoading}
        mainCategories={mainCategories}
      />

      <AddCategoryModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedCategory(null);
        }}
        onSubmit={handleUpdateCategory}
        loading={submitLoading}
        category={selectedCategory}
        isEditMode={true}
        mainCategories={mainCategories}
      />

      <DeleteCategoryModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedCategory(null);
        }}
        category={selectedCategory}
        onConfirm={handleDeleteCategory}
        loading={submitLoading}
      />
    </div>
  );
}

export default Categories;
